import api from './baseApi';
import { langParams } from 'src/i18n/contentLanguage';

/**
 * The loneliness measure in a LONELINESS Circle
 * (docs/briefs/dunbar148-loneliness-plan.md section 5.1): three questions a
 * member answers before, halfway through and after the programme. Every
 * route answers 404 for a Circle without the LONELINESS code.
 */

export type AssessmentWindow = 'BEFORE' | 'MID' | 'AFTER' | 'FOLLOW_UP';

export interface AssessmentInstrumentItem {
  id: string;
  text: string;
}

export interface AssessmentInstrumentScalePoint {
  value: number;
  label: string;
}

export interface AssessmentInstrument {
  key: string;
  version: number;
  language: string;
  title: string;
  instruction: string;
  items: AssessmentInstrumentItem[];
  scale: AssessmentInstrumentScalePoint[];
  score_min: number;
  score_max: number;
}

export interface AssessmentOwnResponse {
  window: AssessmentWindow;
  score: number;
  answers: { item_id: string; value: number }[];
  consent_research: boolean;
  created_at?: string;
}

export interface AssessmentMe {
  instrument: AssessmentInstrument;
  /** Members answer; leaders and co-leaders run the Circle and do not. */
  can_answer: boolean;
  responses: AssessmentOwnResponse[];
}

export interface AssessmentWindowSummary {
  window: AssessmentWindow;
  n: number;
  /** Null while fewer than `min_n` have answered. */
  mean: number | null;
  /** Count per total score, keyed by the score; null while withheld. */
  distribution: Record<string, number> | null;
}

export interface AssessmentSummary {
  instrument: {
    key: string;
    version: number;
    score_min: number;
    score_max: number;
  };
  min_n: number;
  members: number;
  windows: AssessmentWindowSummary[];
  change_before_after: { n: number; mean: number | null };
}

export interface AssessmentSubmission {
  window: AssessmentWindow;
  answers: Record<string, number>;
  consent_service: true;
  consent_research: boolean;
}

type ApiEnvelope<T> = { result: T };

const circlePath = (circleId: string): string =>
  `tribes/${encodeURIComponent(circleId)}/assessments`;

export const assessmentsApi = {
  /** The questions and the caller's own answers so far. */
  getMe: async (circleId: string): Promise<AssessmentMe> => {
    const { data } = await api.get<ApiEnvelope<AssessmentMe>>(
      `${circlePath(circleId)}/me`,
      { params: langParams() }
    );
    return data.result;
  },

  /** A member's answers for one window; the API refuses a second answer. */
  submit: async (
    circleId: string,
    submission: AssessmentSubmission
  ): Promise<AssessmentOwnResponse> => {
    const { data } = await api.post<ApiEnvelope<AssessmentOwnResponse>>(
      `${circlePath(circleId)}/responses`,
      submission
    );
    return data.result;
  },

  /** The member's standing word on research use, applied to all their answers. */
  setResearchConsent: async (
    circleId: string,
    consentResearch: boolean
  ): Promise<void> => {
    await api.patch(`${circlePath(circleId)}/research-consent`, {
      consent_research: consentResearch,
    });
  },

  /** For the Circle's leaders: the group's numbers, withheld under `min_n`. */
  getSummary: async (circleId: string): Promise<AssessmentSummary> => {
    const { data } = await api.get<ApiEnvelope<AssessmentSummary>>(
      `${circlePath(circleId)}/summary`
    );
    return data.result;
  },
};
